import { useEffect, useRef, useState } from 'react';
import {
  CREATOR_ORDER_V2_ERROR_CODE,
  isCreatorOrderV2ApiError
} from '@/apis/creatorOrderV2/errors';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import type {
  CreatorMoneyRange,
  CreatorPackagePriceRecommendationRequest,
  CreatorPackagePriceRecommendationResponse
} from '@/interface/creatorOrderV2';

export type PackagePriceRecommendationStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'unavailable'
  | 'unsupported'
  | 'error';

export type PackagePriceRecommendationState = {
  status: PackagePriceRecommendationStatus;
  range: CreatorMoneyRange | null;
  recommendation: CreatorPackagePriceRecommendationResponse | null;
  error: unknown;
};

const INITIAL_STATE: PackagePriceRecommendationState = {
  status: 'idle',
  range: null,
  recommendation: null,
  error: null
};

export const getPackagePriceRecommendationFailureStatus = (
  error: unknown
): PackagePriceRecommendationStatus => {
  if (!isCreatorOrderV2ApiError(error)) return 'error';
  if (
    error.code ===
    CREATOR_ORDER_V2_ERROR_CODE.PACKAGE_PRICE_PREDICTION_UNAVAILABLE
  ) {
    return 'unavailable';
  }
  if (
    error.code ===
    CREATOR_ORDER_V2_ERROR_CODE.PACKAGE_PRICE_CONTENT_TYPE_UNSUPPORTED
  ) {
    return 'unsupported';
  }
  return 'error';
};

export default function usePackagePriceRecommendation({
  orderId,
  channels,
  enabled,
  debounceMs = 250
}: {
  orderId: string;
  channels: CreatorPackagePriceRecommendationRequest['channels'];
  enabled: boolean;
  debounceMs?: number;
}) {
  const [state, setState] = useState<
    PackagePriceRecommendationState & { selectionKey?: string }
  >(INITIAL_STATE);
  const requestSequenceRef = useRef(0);
  const requestKey = JSON.stringify(channels);
  const selectionKey = `${orderId}:${requestKey}`;
  const canRequest = Boolean(
    enabled &&
      orderId &&
      channels.length &&
      channels.every(({ channelKey, contentType }) => channelKey && contentType)
  );

  useEffect(() => {
    let active = true;
    const requestSequence = ++requestSequenceRef.current;
    const requestChannels = JSON.parse(
      requestKey
    ) as CreatorPackagePriceRecommendationRequest['channels'];
    if (
      !enabled ||
      !orderId ||
      requestChannels.length === 0 ||
      requestChannels.some(({ channelKey, contentType }) =>
        Boolean(!channelKey || !contentType)
      )
    ) {
      setState(INITIAL_STATE);
      return undefined;
    }

    setState({
      ...INITIAL_STATE,
      selectionKey,
      status: 'loading'
    });

    const timer = window.setTimeout(async () => {
      try {
        const result = await creatorOrderV2Api.packagePriceRecommendation(
          orderId,
          { channels: requestChannels }
        );
        if (!active || requestSequence !== requestSequenceRef.current) return;
        setState({
          selectionKey,
          status: 'success',
          range: result.data.recommendedRange,
          recommendation: result.data,
          error: null
        });
      } catch (error) {
        if (!active || requestSequence !== requestSequenceRef.current) return;
        setState({
          selectionKey,
          status: getPackagePriceRecommendationFailureStatus(error),
          range: null,
          recommendation: null,
          error
        });
      }
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      active = false;
    };
  }, [debounceMs, enabled, orderId, requestKey, selectionKey]);

  // Do not render the previous selection's prices while the next effect starts.
  if (!canRequest) return INITIAL_STATE;
  return state.selectionKey === selectionKey
    ? state
    : { ...INITIAL_STATE, status: 'loading' as const };
}
